import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { straightMiles } from "./gpsUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Driving distance (Stadia) per unit, refreshed every 10s; local straight-line fallback.
export function useUnitDistances(lat, lng, gpsPositions) {
  const [remote, setRemote] = useState({});
  const posRef = useRef(gpsPositions);
  posRef.current = gpsPositions;

  useEffect(() => {
    setRemote({});
    if (lat == null || lng == null) return undefined;
    let cancelled = false;
    const load = () => {
      axios.post(`${API}/gps/distances`, { lat, lng })
        .then((r) => { if (!cancelled) setRemote(r.data || {}); })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 10000);
    return () => { cancelled = true; clearInterval(t); };
  }, [lat, lng]);

  return useMemo(() => {
    const out = {};
    if (lat == null || lng == null) return out;
    (gpsPositions || []).forEach((p) => {
      const uid = p.mapped_unit_id;
      if (!uid) return;
      const r = remote[uid];
      if (r && r.mode === "driving") out[uid] = { miles: r.miles, minutes: r.minutes, mode: "driving", status: p.status };
      else {
        const m = straightMiles(lat, lng, p.latitude, p.longitude);
        if (m != null) out[uid] = { miles: m, minutes: null, mode: "straight", status: p.status };
      }
    });
    return out;
  }, [lat, lng, gpsPositions, remote]);
}

export function sortByDistance(units, dist) {
  return [...units].sort((a, b) => {
    const da = dist[a.id]?.miles, db = dist[b.id]?.miles;
    if (da == null && db == null) return a.name.localeCompare(b.name);
    if (da == null) return 1;
    if (db == null) return -1;
    return da - db;
  });
}

export function DistanceBadge({ d, testId, dark = false }) {
  if (!d) return null;
  const driving = d.mode === "driving";
  return (
    <span data-testid={testId} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, fontWeight: 700, color: dark ? "#00CC66" : "#006600" }}>
      {d.miles.toFixed(1)} mi
      {driving && d.minutes != null && <span style={{ fontWeight: 500, color: dark ? "#8FD" : "#336" }}>~{Math.max(1, Math.round(d.minutes))}m</span>}
      <span style={{ fontSize: 9, letterSpacing: 0.5, padding: "0 3px", borderRadius: 2, background: driving ? "#0A5" : "#C80", color: "#FFF" }}>
        {driving ? "DRIVE" : "STRAIGHT-LINE"}
      </span>
      {d.status && d.status !== "live" && <span style={{ fontSize: 9, color: "#C33" }}>{d.status.toUpperCase()}</span>}
    </span>
  );
}
